import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, InternalAxiosRequestConfig } from 'axios'
import i18n from '@/shared/i18n/config'
import { useAuthStore } from '@/stores/authStore'

type ErrorHandler = (message: string, error?: unknown) => void
let errorHandler: ErrorHandler = (message, error) => {
  console.error('API Error:', message, error)
}

export const setErrorHandler = (handler: ErrorHandler) => {
  errorHandler = handler
}

const tError = (key: string, options?: Record<string, unknown>): string => {
  return i18n.t(key, { ns: 'errors', ...options }) || key
}

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api/v1'

// Token 刷新状态管理（防止并发刷新）
let isRefreshing = false
let refreshSubscribers: Array<(token: string) => void> = []

const onTokenRefreshed = (newToken: string) => {
  refreshSubscribers.forEach(cb => cb(newToken))
  refreshSubscribers = []
}

const addRefreshSubscriber = (cb: (token: string) => void) => {
  refreshSubscribers.push(cb)
}

const getToken = (): string | null => {
  return useAuthStore.getState().token
}

const getRefreshToken = (): string | null => {
  return useAuthStore.getState().refreshToken
}

const setTokens = (accessToken: string, refreshToken?: string | null) => {
  const store = useAuthStore.getState()
  const update: Record<string, unknown> = { token: accessToken }
  if (refreshToken) {
    update.refreshToken = refreshToken
  }
  useAuthStore.setState(update)
  // 同步到独立 localStorage key（兼容旧逻辑）
  localStorage.setItem('access_token', accessToken)
  if (refreshToken) {
    localStorage.setItem('refresh_token', refreshToken)
  }
}

const clearAuthState = () => {
  useAuthStore.setState({
    user: null,
    company: null,
    token: null,
    refreshToken: null,
    isAuthenticated: false,
    isLoading: false,
    error: null,
  })
  localStorage.removeItem('access_token')
  localStorage.removeItem('refresh_token')
}

const createAxiosInstance = (): AxiosInstance => {
  const instance = axios.create({
    baseURL: API_BASE_URL,
    timeout: 30000,
    headers: {
      'Content-Type': 'application/json',
    },
  })

  instance.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      const token = getToken()
      if (token) {
        config.headers.Authorization = `Bearer ${token}`
      }
      config.headers['X-Request-ID'] = Date.now().toString(36) + Math.random().toString(36).substr(2)
      return config
    },
    (error) => {
      return Promise.reject(error)
    }
  )

  instance.interceptors.response.use(
    (response: AxiosResponse) => {
      const { data } = response

      if (data && data.code !== undefined && data.code !== 0) {
        const errorMsg = data.message || tError('api.requestFailed')
        errorHandler(errorMsg)
        return Promise.reject(new Error(errorMsg))
      }

      return data.data !== undefined ? data.data : data
    },
    async (error) => {
      const originalRequest = error.config
      let errorMessage = tError('api.networkError')

      if (error.response) {
        const { status, data } = error.response
        const requestUrl = originalRequest?.url || ''
        const isAuthRequest = /\/auth\/(login|register|refresh)/.test(requestUrl)

        if (status === 401 && !isAuthRequest && !originalRequest._retry) {
          const refreshToken = getRefreshToken()

          if (refreshToken) {
            if (isRefreshing) {
              return new Promise((resolve) => {
                addRefreshSubscriber((newToken: string) => {
                  originalRequest.headers.Authorization = `Bearer ${newToken}`
                  resolve(instance(originalRequest))
                })
              })
            }

            isRefreshing = true
            originalRequest._retry = true

            try {
              const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {
                refresh_token: refreshToken
              })

              const newToken = response.data?.data?.access_token || response.data?.access_token
              if (newToken) {
                const newRefreshToken = response.data?.data?.refresh_token || response.data?.refresh_token
                setTokens(newToken, newRefreshToken)

                onTokenRefreshed(newToken)
                originalRequest.headers.Authorization = `Bearer ${newToken}`
                return instance(originalRequest)
              }
            } catch {
              // refresh 失败，走 logout 流程
            } finally {
              isRefreshing = false
            }
          }

          // 无 refresh token 或 refresh 失败 → 清除登录状态
          clearAuthState()

          if (window.location.pathname !== '/login') {
            window.location.href = '/login'
          }

          return Promise.reject(new Error(tError('api.unauthorized')))
        }

        switch (status) {
          case 400:
            errorMessage = data?.message || tError('api.badRequest')
            break
          case 401:
            errorMessage = data?.message || data?.detail || tError('api.unauthorized')
            break
          case 403:
            errorMessage = tError('api.forbidden')
            break
          case 404:
            errorMessage = tError('api.notFound')
            break
          case 500:
            errorMessage = tError('api.serverError')
            break
          case 502:
          case 503:
          case 504:
            errorMessage = tError('api.serviceUnavailable')
            break
          default:
            errorMessage = data?.message || tError('api.requestFailedWithStatus', { status })
        }
      } else if (error.request) {
        errorMessage = tError('api.networkDisconnected')
      } else {
        errorMessage = error.message || tError('api.configError')
      }

      // auth 请求的错误由调用方自行处理（LoginPage 显示 Alert），不触发全局弹窗
      const requestUrl = originalRequest?.url || ''
      const isAuthEndpoint = /\/auth\/(login|register|refresh)/.test(requestUrl)
      if (!isAuthEndpoint) {
        errorHandler(errorMessage, error)
      }

      console.error('API请求错误:', {
        message: errorMessage,
        config: error.config,
        response: error.response,
      })

      return Promise.reject(new Error(errorMessage))
    }
  )

  return instance
}

export const apiClient = createAxiosInstance()

export const api = {
  get: <T = unknown>(url: string, config?: AxiosRequestConfig): Promise<T> =>
    apiClient.get(url, config),

  post: <T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> =>
    apiClient.post(url, data, config),

  put: <T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> =>
    apiClient.put(url, data, config),

  patch: <T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> =>
    apiClient.patch(url, data, config),

  delete: <T = unknown>(url: string, config?: AxiosRequestConfig): Promise<T> =>
    apiClient.delete(url, config),

  upload: <T = unknown>(url: string, file: File, onProgress?: (progress: number) => void): Promise<T> => {
    const formData = new FormData()
    formData.append('file', file)

    return apiClient.post(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total)
          onProgress(progress)
        }
      },
    })
  },
}

export type { AxiosRequestConfig, AxiosResponse }
